import React, { useMemo } from 'react'
import { Flex, Table, Thead, Th, Tbody, Tr, Td } from '@chakra-ui/react'
import { ArrowDownIcon, ArrowUpIcon } from '@blueprinthq/joy'
import { useTable, useSortBy, CellProps } from 'react-table'
import { CheckInDtoV2, CheckInScoreDtoV1 } from '~/clinician-api/models'
import { mapCheckInAnswerToValue } from './utils'

interface Answer {
  index: number
  label: string | undefined
  value: string | undefined
}

export const WorksheetAnswersTable = ({
  score,
  checkIn
}: {
  score: CheckInScoreDtoV1
  checkIn: CheckInDtoV2
}) => {
  const answers: Answer[] = useMemo(() => {
    return checkIn.content.nodes.filter((n) => n.type !== 'heading').map((n, i) => {
      const answer = score.answers.find(a => n.id === a.key)

      return {
        index: i,
        label: n?.label,
        value: mapCheckInAnswerToValue(answer, n)
      }
    })
  }, [score, checkIn])

  const columns = useMemo(
    () => [
      {
        Header: 'Question',
        accessor: 'index',
        Cell: ({ row, value }: CellProps<Answer>) => {
          return `${value + 1}. ${row.original.label}`
        },
        style: {
          width: 500
        },
        id: 'index'
      },
      {
        Header: 'Answer',
        accessor: 'value',
        style: {
          width: 400
        }
      }
    ],
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable<Answer>(
    {
      // @ts-ignore
      columns,
      data: answers,
      disableSortRemove: true,
      initialState: {
        sortBy: [{ id: 'index', desc: false }]
      }
    },
    useSortBy
  )

  return (
    <Table {...getTableProps()}>
      <Thead>
        {headerGroups.map((headerGroup, i) => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) => (
              <Th
                {...column.getHeaderProps(column.getSortByToggleProps())}
                borderColor="light_gray"
                // @ts-ignore
                sx={column.style}
              >
                <Flex alignItems="center">
                  {column.render('Header')}
                  <span>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <ArrowDownIcon size="sm" fill={'gray.600'} />
                      ) : (
                        <ArrowUpIcon size="sm" fill={'gray.600'} />
                      )
                    ) : (
                      ''
                    )}
                  </span>
                </Flex>
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <Tr
              {...row.getRowProps()}
              bg={i % 2 === 0 ? 'transparent' : 'rgba(45, 84, 232, 0.04)'}
            >
              {row.cells.map((cell, j) => (
                <Td
                  {...cell.getCellProps()}
                  borderColor="light_gray"
                  verticalAlign="middle"
                  pt="small"
                  pb="small"
                >
                  {cell.render('Cell')}
                </Td>
              ))}
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}
